import request from '../utils/request';

export type ImportScenario = 'upload_immediate' | 'upload_after_pack' | 'warehouse_provide';

export type InstructionImportMappingProfile = {
  id: number;
  source_type: 'excel_csv' | 'erp' | 'platform';
  scenario: ImportScenario;
  name: string;
  field_mapping: Record<string, string>;
  transform_rules?: Record<string, unknown>;
  is_default?: boolean;
  is_active?: boolean;
  created_at?: string;
  updated_at?: string;
};

export type InstructionImportJob = {
  id: number;
  source_type: 'excel_csv' | 'erp' | 'platform';
  scenario: ImportScenario;
  status: 'draft' | 'queued' | 'running' | 'done' | 'failed' | 'cancelled';
  file_name: string;
  mapping_profile?: number | null;
  mapping_profile_name?: string;
  field_mapping_snapshot?: Record<string, string>;
  transform_rules_snapshot?: Record<string, unknown>;
  summary?: Record<string, unknown>;
  error_message?: string;
  created_at?: string;
  updated_at?: string;
};

export type InstructionImportJobItem = {
  id: number;
  row_no: number;
  status: 'pending' | 'created' | 'failed' | 'skipped';
  created_instruction?: number | null;
  created_instruction_system_code?: string;
  error_message?: string;
  mapped_row?: Record<string, unknown>;
  meta?: Record<string, unknown>;
};

export type InstructionImportPrecheckResult = {
  ok: boolean;
  blocking_count: number;
  warning_count: number;
  blocking_issues: Array<{ row_no: number; type: string; message: string }>;
  warnings: Array<{ row_no?: number; type: string; message: string }>;
  match_groups: Array<{
    serial_no: string;
    row_nos: number[];
    expected_file_name: string;
    status: string;
    matched_file_name?: string;
    match_reason?: string;
    issue_text?: string;
  }>;
  // 只有后端检测到"上传的是一份多页合并运单PDF"并按页拆分时才会有内容——前端拿到后
  // 要用这批文件顶替浏览器里原来那份合并PDF，作为后续创建导入任务时真正提交的运单附件
  // （执行阶段只按文件名匹配，不会做条码识别，必须让它看到的就是已经拆好命名的文件）。
  generated_waybill_files?: Array<{ name: string; content_base64: string }>;
  inventory_summary: Array<{
    warehouse_code: string;
    inventory_unit_code: string;
    inventory_unit_sorting_code?: string;
    inventory_unit_custom_code?: string;
    inventory_unit_tag_display?: string;
    available_qty: number;
    required_qty: number;
    row_nos: number[];
  }>;
  debug_report?: {
    enabled: boolean;
    stage_counts: Record<string, number>;
    unmatched_reason_counts: Record<string, number>;
    groups: Array<{
      serial_no: string;
      row_nos: number[];
      final_status: string;
      final_reason?: string;
      matched_file_name?: string;
      attempts: Array<{
        stage: string;
        result: string;
        reason?: string;
        matched_file_name?: string;
      }>;
    }>;
    attachments: Array<{
      file_name: string;
      mime?: string;
      parse_invoked: boolean;
      parse_error?: string;
      pdf_text_chars?: number;
      pdf_text_preview?: string;
      barcode_numbers?: string[];
      ocr_text_chars?: number;
      ocr_text_preview?: string;
      ocr_confidence?: number | null;
    }>;
  };
};

const unwrapList = <T>(raw: any): T[] => {
  if (Array.isArray(raw)) return raw as T[];
  if (Array.isArray(raw?.results)) return raw.results as T[];
  return [];
};

export const listInstructionImportMappingProfiles = async (params: {
  source_type?: 'excel_csv' | 'erp' | 'platform';
  scenario?: ImportScenario;
  // 管理员代客户操作时，传入目标客户的 user id——后端只在当前登录账号本身是管理员、
  // 且这个 id 真实对应一个客户主账号时才会采用，见 resolve_import_tenant_id。
  acting_as_customer_id?: number;
}) => {
  const resp = await request.get('/api/v1/instruction/import-mapping-profiles/', { params });
  return unwrapList<InstructionImportMappingProfile>(resp.data);
};

export const createInstructionImportMappingProfile = async (payload: {
  source_type: 'excel_csv' | 'erp' | 'platform';
  scenario: ImportScenario;
  name: string;
  field_mapping: Record<string, string>;
  transform_rules?: Record<string, unknown>;
  acting_as_customer_id?: number;
}) => {
  const { acting_as_customer_id, ...body } = payload;
  const resp = await request.post('/api/v1/instruction/import-mapping-profiles/', body, {
    params: acting_as_customer_id ? { acting_as_customer_id } : undefined,
  });
  return resp.data as InstructionImportMappingProfile;
};

export type ResolveProjectByTagResult =
  | { tag: string; status: 'resolved'; project_id: number; project_code: string; project_name: string }
  | { tag: string; status: 'ambiguous'; candidates: Array<{ project_id: number; project_code: string; project_name: string }> }
  | { tag: string; status: 'not_found' }
  | { tag: string; status: 'error'; message?: string };

export const resolveProjectByTag = async (tags: string[], actingAsCustomerId?: number) => {
  const resp = await request.post('/api/v1/instruction/import-jobs/resolve-project-by-tag/', { tags }, {
    params: actingAsCustomerId ? { acting_as_customer_id: actingAsCustomerId } : undefined,
  });
  return (resp.data?.results || []) as ResolveProjectByTagResult[];
};

export const updateInstructionImportMappingProfile = async (
  id: number,
  payload: {
    name: string;
    field_mapping: Record<string, string>;
    transform_rules?: Record<string, unknown>;
  },
  actingAsCustomerId?: number,
) => {
  const resp = await request.patch(`/api/v1/instruction/import-mapping-profiles/${id}/`, payload, {
    params: actingAsCustomerId ? { acting_as_customer_id: actingAsCustomerId } : undefined,
  });
  return resp.data as InstructionImportMappingProfile;
};

export const deleteInstructionImportMappingProfile = async (id: number, actingAsCustomerId?: number) => {
  await request.delete(`/api/v1/instruction/import-mapping-profiles/${id}/`, {
    params: actingAsCustomerId ? { acting_as_customer_id: actingAsCustomerId } : undefined,
  });
};

export const createInstructionImportJob = async (payload: {
  source_type: 'excel_csv' | 'erp' | 'platform';
  scenario: ImportScenario;
  file_name: string;
  file?: File;
  waybillBundleFiles?: File[];
  mapping_profile?: number;
  field_mapping_snapshot?: Record<string, string>;
  transform_rules_snapshot?: Record<string, unknown>;
  summary?: Record<string, unknown>;
  meta?: Record<string, unknown>;
  // 管理员代客户操作时，传入目标客户的 user id——跟映射配置（阶段一）是同一套机制，
  // 见 resolve_import_tenant_id。
  acting_as_customer_id?: number;
}) => {
  const form = new FormData();
  form.append('source_type', payload.source_type);
  form.append('scenario', payload.scenario);
  form.append('file_name', payload.file_name || '');
  if (payload.file) {
    form.append('file', payload.file);
  }
  const waybillFiles = (payload.waybillBundleFiles || []).filter(Boolean);
  if (waybillFiles.length > 0) {
    let waybillBundleFile: File;
    if (waybillFiles.length === 1) {
      waybillBundleFile = waybillFiles[0];
    } else {
      const mod = await import('jszip');
      const JSZip = (mod as any).default || mod;
      const zip = new JSZip();
      const duplicateNameCounter = new Map<string, number>();
      waybillFiles.forEach((file) => {
        const originalName = String(file.name || 'attachment');
        const count = duplicateNameCounter.get(originalName) || 0;
        duplicateNameCounter.set(originalName, count + 1);
        const safeName = count === 0 ? originalName : `${count + 1}_${originalName}`;
        zip.file(safeName, file);
      });
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const bundleName = `waybill_attachments_${Date.now()}.zip`;
      waybillBundleFile = new File([zipBlob], bundleName, { type: 'application/zip' });
    }
    form.append('waybill_bundle_file', waybillBundleFile);
    form.append('waybill_bundle_size', String(waybillBundleFile.size || 0));
  }
  if (payload.mapping_profile) {
    form.append('mapping_profile', String(payload.mapping_profile));
  }
  if (payload.field_mapping_snapshot) {
    form.append('field_mapping_snapshot', JSON.stringify(payload.field_mapping_snapshot));
  }
  if (payload.transform_rules_snapshot) {
    form.append('transform_rules_snapshot', JSON.stringify(payload.transform_rules_snapshot));
  }
  if (payload.summary) {
    form.append('summary', JSON.stringify(payload.summary));
  }
  if (payload.meta) {
    form.append('meta', JSON.stringify(payload.meta));
  }
  const resp = await request.post('/api/v1/instruction/import-jobs/', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    params: payload.acting_as_customer_id ? { acting_as_customer_id: payload.acting_as_customer_id } : undefined,
  });
  return resp.data as InstructionImportJob;
};

export const listInstructionImportJobs = async (params: {
  source_type?: 'excel_csv' | 'erp' | 'platform';
  scenario?: ImportScenario;
  acting_as_customer_id?: number;
}) => {
  const resp = await request.get('/api/v1/instruction/import-jobs/', { params });
  return unwrapList<InstructionImportJob>(resp.data);
};

export const executeInstructionImportJob = async (
  jobId: number,
  payload?: { mode?: 'all' | 'failed_only' | 'skipped_force' },
  actingAsCustomerId?: number,
) => {
  const resp = await request.post(`/api/v1/instruction/import-jobs/${jobId}/execute/`, payload || {}, {
    params: actingAsCustomerId ? { acting_as_customer_id: actingAsCustomerId } : undefined,
  });
  return resp.data as {
    job_id: number;
    status: string;
    created_count: number;
    failed_count: number;
    execute_mode?: 'all' | 'failed_only' | 'skipped_force';
  };
};

export const listInstructionImportJobItems = async (jobId: number, actingAsCustomerId?: number) => {
  const resp = await request.get(`/api/v1/instruction/import-jobs/${jobId}/items/`, {
    params: actingAsCustomerId ? { acting_as_customer_id: actingAsCustomerId } : undefined,
  });
  return unwrapList<InstructionImportJobItem>(resp.data);
};

export const cancelInstructionImportJob = async (jobId: number, actingAsCustomerId?: number) => {
  const resp = await request.post(`/api/v1/instruction/import-jobs/${jobId}/cancel/`, {}, {
    params: actingAsCustomerId ? { acting_as_customer_id: actingAsCustomerId } : undefined,
  });
  return resp.data as { job_id: number; status: string; cancel_requested: boolean };
};

export const precheckInstructionImport = async (payload: {
  scenario: ImportScenario;
  rows: Array<Record<string, unknown>>;
  waybillBundleFiles?: File[];
  acting_as_customer_id?: number;
}) => {
  const form = new FormData();
  form.append('scenario', payload.scenario);
  form.append('rows', JSON.stringify(payload.rows || []));
  const waybillFiles = (payload.waybillBundleFiles || []).filter(Boolean);
  if (waybillFiles.length > 0) {
    let waybillBundleFile: File;
    if (waybillFiles.length === 1) {
      waybillBundleFile = waybillFiles[0];
    } else {
      const mod = await import('jszip');
      const JSZip = (mod as any).default || mod;
      const zip = new JSZip();
      const duplicateNameCounter = new Map<string, number>();
      waybillFiles.forEach((file) => {
        const originalName = String(file.name || 'attachment');
        const count = duplicateNameCounter.get(originalName) || 0;
        duplicateNameCounter.set(originalName, count + 1);
        const safeName = count === 0 ? originalName : `${count + 1}_${originalName}`;
        zip.file(safeName, file);
      });
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const bundleName = `waybill_precheck_${Date.now()}.zip`;
      waybillBundleFile = new File([zipBlob], bundleName, { type: 'application/zip' });
    }
    form.append('waybill_bundle_file', waybillBundleFile);
  }
  // 预审可能包含 PDF 文本/条码/OCR 解析，耗时显著高于普通接口。
  // 这里按数据量动态放宽超时：最短 5 分钟，最长 10 分钟。
  const rowCount = Array.isArray(payload.rows) ? payload.rows.length : 0;
  const timeoutMs = (() => {
    if (payload.scenario !== 'upload_immediate') return 300000;
    const dynamic = 300000 + (waybillFiles.length * 800) + (rowCount * 20);
    return Math.max(300000, Math.min(600000, dynamic));
  })();
  const resp = await request.post('/api/v1/instruction/import-jobs/precheck/', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: timeoutMs,
    params: payload.acting_as_customer_id ? { acting_as_customer_id: payload.acting_as_customer_id } : undefined,
  });
  return resp.data as InstructionImportPrecheckResult;
};

export const exportFailedRowsCsv = async (jobId: number, actingAsCustomerId?: number) => {
  const resp = await request.get(`/api/v1/instruction/import-jobs/${jobId}/export-failed-csv/`, {
    responseType: 'blob',
    params: actingAsCustomerId ? { acting_as_customer_id: actingAsCustomerId } : undefined,
  } as any);
  return resp.data as Blob;
};
